import { type MutableRefObject, useCallback, useEffect, useRef } from 'react';
import type { User } from '../../../../api/types';
import { useErrorContext } from '../../../../errorHandler/context/ErrorContext';
import type { AppError } from '../../../../errorHandler/errors';
import { useUsers } from './useUsers';

interface Args {
  lastElementRef: MutableRefObject<HTMLDivElement | null>;
  searchTerm?: string;
}

type InfiniteScrollType = {
  data?: User[];
  isFetchingNextPage: boolean;
  refetch: () => void;
  isLoading: boolean;
  hasNextPage: boolean;
  error?: AppError;
};

const useInfiniteScroll = ({ lastElementRef, searchTerm }: Args): InfiniteScrollType => {
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, refetch, isLoading, error } =
    useUsers(searchTerm || '');
  const { emitError } = useErrorContext();
  const observerElem = useRef<IntersectionObserver | null>(null);
  const lastErrorRef = useRef<string | null>(null);

  useEffect(() => {
    if (error && (lastErrorRef.current === null || lastErrorRef.current !== error.message)) {
      lastErrorRef.current = error?.message;
      console.debug('Error fetching users: ', error);
      emitError(error as AppError);
    }
  }, [error, emitError]);

  const observerCallback = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
        void fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage],
  );

  const observer = useCallback(
    (node: HTMLDivElement) => {
      if (observerElem.current) {
        observerElem.current.disconnect();
      }
      observerElem.current = new IntersectionObserver(observerCallback, { rootMargin: '400px' });
      if (node) {
        observerElem.current.observe(node);
      }
    },
    [observerCallback],
  );

  useEffect(() => {
    const currentElement = lastElementRef.current;
    if (currentElement) {
      observer(currentElement);
    }
    return () => {
      observerElem.current?.disconnect();
      observerElem.current = null;
    };
  }, [lastElementRef, observer]);

  return { data, isFetchingNextPage, refetch, isLoading, hasNextPage, error: error as AppError };
};

export default useInfiniteScroll;
